import { useState, useRef, useEffect } from "react";
import { Sparkles, Globe, Check, X, Plus } from "lucide-react";
import { PlatformIcon, type Platform } from "@/shared/ui";
import { useDraftStore } from "@/shared/store/draft";
import { useCategories, useGeoRegions, useProductParseUrl } from "@/shared/api/wb";
import { Button, Card, Field, InfoTip } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import { matchLabel } from "@/shared/lib/fuzzyMatch";
import { StepHead } from "../StepHead";
import { WizardLayout } from "../WizardLayout";

const TYPES = [
  { id: "product", label: "Товар" },
  { id: "service", label: "Услуга" },
  { id: "app",     label: "Приложение" },
  { id: "event",   label: "Мероприятие" },
  { id: "other",   label: "Другое" },
];

export function ProductStep() {
  const { draft, autofillState, patchDraft, setAutofillState, setGeo } = useDraftStore();
  const { branch, setBranch } = useDraftStore();
  const cats = useCategories().data ?? [];
  const filled = draft.productAutofilled;
  const [manual, setManual] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseSource, setParseSource] = useState<"site" | "websearch" | "unknown" | null>(null);
  const parse = useProductParseUrl();

  async function runAutofill(url?: string) {
    const target = (url ?? draft.productUrl).trim();
    if (!target) return;
    setParseError(null);
    setParseSource(null);
    setAutofillState("fetching");
    patchDraft({ productUrl: target, productAutofilled: false });
    try {
      const r = await parse.mutateAsync(target);
      patchDraft({
        productAutofilled: true,
        productName: r.product_name,
        productCategory: r.product_category,
        productType: r.product_type,
        productDesc: r.product_desc,
      });
      setParseSource(r.source);
      setAutofillState("done");
    } catch {
      setParseError("Не удалось разобрать ссылку. Проверьте URL или заполните вручную.");
      setAutofillState("idle");
    }
  }

  return (
    <WizardLayout>
      <StepHead step="01" title="Продукт" sub="Вставьте ссылку — заполним название, категорию и описание автоматически." />

      <Card className="!p-7 mb-5">
        <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-3.5">Ссылка на продукт</div>
        <div className="flex gap-3 items-stretch">
          <input
            type="url"
            placeholder="https://…"
            value={draft.productUrl}
            onChange={(e) => { patchDraft({ productUrl: e.target.value, productAutofilled: false }); if (autofillState !== "idle") setAutofillState("idle"); }}
            onKeyDown={(e) => e.key === "Enter" && runAutofill()}
            className="flex-1 h-[60px] px-5 rounded-pill border border-ink-12 bg-cream text-[17px] focus:border-ink outline-none placeholder:text-ink-40"
          />
          <Button onClick={() => runAutofill()} disabled={autofillState === "fetching" || !draft.productUrl}
            className="!h-[60px] !px-7 !text-[15px]">
            {autofillState === "fetching" ? "Применяем…" : "Применить"} <Sparkles size={16} />
          </Button>
        </div>
        <div className="mt-3 flex justify-end items-center">
          <button onClick={() => { setManual(true); setAutofillState("done"); }} className="text-[13px] text-ink-40 bg-transparent border-0 cursor-pointer hover:text-ink">Заполнить вручную →</button>
        </div>
      </Card>

      {autofillState === "fetching" && (
        <div className="flex justify-center py-3 mb-4 fade-up">
          <img src="/assets/mascot-flower-1.svg" alt="Загрузка" className="w-14 h-14 mascot-bob" />
        </div>
      )}

      {parseError && (
        <div className="px-4 py-2.5 mb-4 rounded-md bg-danger/10 text-danger text-[13px] fade-up">
          {parseError}
        </div>
      )}

      {filled && parseSource === "unknown" && (
        <div className="px-4 py-3 mb-4 rounded-md bg-warning/15 text-warning text-[13px] leading-relaxed fade-up">
          <b>Не нашли продукт ни на сайте, ни в открытых источниках.</b> Чтобы не выдумывать,
          поля ниже оставили частично пустыми — заполните описание вручную.
        </div>
      )}

      {(filled || manual) && (
        <div className="grid gap-3 mb-7 fade-up">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
              {filled ? "AI заполнил — можно отредактировать" : "Заполните вручную"}
            </div>
            {filled && parseSource === "websearch" && (
              <span className="text-[10.5px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-pill bg-link/15 text-link">
                найдено в поиске
              </span>
            )}
            {filled && parseSource === "unknown" && (
              <span className="text-[10.5px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-pill bg-warning/15 text-warning">
                не распознано
              </span>
            )}
          </div>
          <Field label="Название" value={draft.productName} onChange={(e) => patchDraft({ productName: e.target.value })} />
          <div>
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-1.5">Тип продукта</div>
            <select
              value={draft.productType}
              onChange={(e) => patchDraft({ productType: e.target.value })}
              className="h-10 w-full px-3 rounded-md border border-ink-12 bg-white text-[14px] focus:border-ink outline-none"
            >
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {draft.productType === "other" && (
              <input
                type="text"
                placeholder="Введите тип продукта"
                value={draft.productTypeCustom}
                onChange={(e) => patchDraft({ productTypeCustom: e.target.value })}
                className="mt-2.5 w-full h-10 px-4 rounded-md border border-ink-12 bg-white text-[14px] focus:border-ink outline-none placeholder:text-ink-40"
              />
            )}
          </div>
          <CategoryPicker
            value={draft.productCategory}
            customValue={draft.productCategoryCustom}
            onChange={(v) => patchDraft({ productCategory: v })}
            onCustomChange={(v) => patchDraft({ productCategoryCustom: v })}
            categories={cats}
          />
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">Описание</span>
            <textarea value={draft.productDesc} onChange={(e) => patchDraft({ productDesc: e.target.value })} rows={3}
              className="px-4 py-3 rounded-md border border-ink-12 bg-white text-[15px] focus:border-ink outline-none resize-none" />
          </label>
        </div>
      )}

      <GeoBlock geo={draft.geo} setGeo={setGeo} />
      <FormatBlock type={branch.campaignType} setType={(t) => setBranch("campaignType", t)} />

    </WizardLayout>
  );
}

const CUSTOM_CATEGORY_ID = "__custom__";

function CategoryPicker({
  value, customValue, onChange, onCustomChange, categories,
}: {
  value: string;
  customValue: string;
  onChange: (v: string) => void;
  onCustomChange: (v: string) => void;
  categories: { id: string; label: string; group?: string }[];
}) {
  const isCustom = value === CUSTOM_CATEGORY_ID;
  const selected = categories.find((c) => c.id === value);

  const [query, setQuery] = useState(selected?.label ?? "");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Keep the input text in sync when the value changes from outside
  // (AI autofill, resetting the form) rather than by picking a suggestion.
  useEffect(() => {
    if (!isCustom) setQuery(selected?.label ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => { setHighlightIdx(-1); }, [query]);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(selected?.label ?? "");
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const q = query.trim();
  const matches = (q
    ? categories
        .map((c) => ({ c, score: matchLabel(q, c.label, c.group) }))
        .filter((x): x is { c: typeof categories[number]; score: number } => x.score !== null)
        .sort((a, b) => a.score - b.score)
        .map((x) => x.c)
    : categories
  ).slice(0, 8);

  function pick(c: { id: string; label: string }) {
    onChange(c.id);
    setQuery(c.label);
    setOpen(false);
  }

  function pickCustom() {
    onChange(CUSTOM_CATEGORY_ID);
    setQuery("");
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIdx >= 0 && matches[highlightIdx]) pick(matches[highlightIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(selected?.label ?? "");
    }
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-1.5">
        Категория ККТУ
        <InfoTip align="left">
          <b>ККТУ</b> — Классификатор категорий товаров и услуг, используемый для разметки рекламных материалов в соответствии с законом о рекламе. Выберите наиболее точную категорию из реестра.
        </InfoTip>
      </div>

      {isCustom ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Введите категорию"
            value={customValue}
            onChange={(e) => onCustomChange(e.target.value)}
            className="h-10 flex-1 px-4 rounded-md border border-ink-12 bg-white text-[14px] focus:border-ink outline-none placeholder:text-ink-40"
          />
          <button type="button" onClick={() => { onChange(""); setQuery(""); }}
            className="text-[12px] text-link hover:underline whitespace-nowrap">
            Выбрать из реестра
          </button>
        </div>
      ) : (
        <div className="relative" ref={wrapRef}>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Начните вводить категорию — например, «вино» или «одежда»"
            className="h-10 w-full px-3 rounded-md border border-ink-12 bg-white text-[14px] focus:border-ink outline-none placeholder:text-ink-40"
          />
          {open && (
            <ul className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-ink-12 rounded-md shadow-lg overflow-y-auto max-h-72">
              {matches.length === 0 && (
                <li className="px-3 py-2.5 text-[12.5px] text-ink-40">Ничего не нашлось</li>
              )}
              {matches.map((c, i) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onPointerDown={(e) => { e.preventDefault(); pick(c); }}
                    className={cn(
                      "w-full text-left px-3 py-2 text-[13px] transition-colors",
                      i === highlightIdx ? "bg-ink text-white" : "hover:bg-ink-6 text-ink",
                    )}
                  >
                    <div>{c.label}</div>
                    {c.group && (
                      <div className={cn("text-[11px]", i === highlightIdx ? "text-white/60" : "text-ink-40")}>
                        {c.group}
                      </div>
                    )}
                  </button>
                </li>
              ))}
              <li className="border-t border-ink-12">
                <button
                  type="button"
                  onPointerDown={(e) => { e.preventDefault(); pickCustom(); }}
                  className="w-full text-left px-3 py-2 text-[13px] text-link hover:bg-ink-6"
                >
                  Не нашли? Ввести своё →
                </button>
              </li>
            </ul>
          )}
        </div>
      )}

      {value && value !== CUSTOM_CATEGORY_ID ? (
        <div className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-40">
          <span className="w-1.5 h-1.5 rounded-full bg-lime inline-block" />
          Код ККТУ: <span className="font-mono">{value}</span>
        </div>
      ) : isCustom && customValue ? (
        <div className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-40">
          <span className="w-1.5 h-1.5 rounded-full bg-lime inline-block" />
          Номер ККТУ будет присвоен автоматически из реестра ККТУ
        </div>
      ) : null}
    </div>
  );
}

const CUSTOM_GEO_PREFIX = "__custom__:";

const RF_REGIONS = [
  "Республика Адыгея", "Республика Алтай", "Республика Башкортостан", "Республика Бурятия",
  "Республика Дагестан", "Республика Ингушетия", "Кабардино-Балкарская Республика",
  "Республика Калмыкия", "Карачаево-Черкесская Республика", "Республика Карелия",
  "Республика Коми", "Республика Крым", "Республика Марий Эл", "Республика Мордовия",
  "Республика Саха (Якутия)", "Республика Северная Осетия — Алания", "Республика Татарстан",
  "Республика Тыва", "Удмуртская Республика", "Республика Хакасия", "Чеченская Республика",
  "Чувашская Республика", "Алтайский край", "Забайкальский край", "Камчатский край",
  "Краснодарский край", "Красноярский край", "Пермский край", "Приморский край",
  "Ставропольский край", "Хабаровский край", "Амурская область", "Архангельская область",
  "Астраханская область", "Белгородская область", "Брянская область", "Владимирская область",
  "Волгоградская область", "Вологодская область", "Воронежская область", "Ивановская область",
  "Иркутская область", "Калининградская область", "Калужская область", "Кемеровская область",
  "Кировская область", "Костромская область", "Курганская область", "Курская область",
  "Ленинградская область", "Липецкая область", "Магаданская область", "Московская область",
  "Мурманская область", "Нижегородская область", "Новгородская область", "Новосибирская область",
  "Омская область", "Оренбургская область", "Орловская область", "Пензенская область",
  "Псковская область", "Ростовская область", "Рязанская область", "Самарская область",
  "Саратовская область", "Сахалинская область", "Свердловская область", "Смоленская область",
  "Тамбовская область", "Тверская область", "Томская область", "Тульская область",
  "Тюменская область", "Ульяновская область", "Челябинская область", "Ярославская область",
  "Москва", "Санкт-Петербург", "Севастополь",
  "Еврейская автономная область", "Ненецкий автономный округ",
  "Ханты-Мансийский автономный округ — Югра", "Чукотский автономный округ",
  "Ямало-Ненецкий автономный округ",
  "Донецкая Народная Республика", "Луганская Народная Республика",
  "Запорожская область", "Херсонская область",
];

function GeoBlock({ geo, setGeo }: { geo: { mode: "all" | "custom"; regions: string[] }; setGeo: (g: { mode: "all" | "custom"; regions: string[] }) => void }) {
  const regions = useGeoRegions().data ?? [];
  const custom = geo.mode === "custom";
  const sel = geo.regions;
  const [customInput, setCustomInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  const apiSel = sel.filter((id) => !id.startsWith(CUSTOM_GEO_PREFIX));
  const customSel = sel.filter((id) => id.startsWith(CUSTOM_GEO_PREFIX));

  const coverage = custom ? regions.filter((r) => apiSel.includes(r.id)).reduce((s, r) => s + r.share, 0) : 100;

  const suggestions = customInput.trim().length > 0
    ? RF_REGIONS.filter((r) => {
        const q = customInput.trim().toLowerCase();
        if (!r.toLowerCase().includes(q)) return false;
        const customId = `${CUSTOM_GEO_PREFIX}${r}`;
        return !sel.includes(customId);
      }).slice(0, 7)
    : [];

  useEffect(() => { setHighlightIdx(-1); }, [customInput]);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const toggleRegion = (id: string) => {
    const next = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    setGeo({ ...geo, regions: next });
  };

  function addCustomRegion(label?: string) {
    const val = (label ?? customInput).trim();
    if (!val) return;
    const id = `${CUSTOM_GEO_PREFIX}${val}`;
    if (!sel.includes(id)) setGeo({ ...geo, regions: [...sel, id] });
    setCustomInput("");
    setShowSuggestions(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") addCustomRegion();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIdx >= 0) addCustomRegion(suggestions[highlightIdx]);
      else addCustomRegion();
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  }

  return (
    <Card className="!p-6">
      <div className="flex items-start justify-between mb-3.5">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
            <Globe size={13} /> География аудитории
          </div>
          <div className="text-[12.5px] text-ink-40 mt-1">По умолчанию — вся Россия. Можно сузить до конкретных регионов.</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-[18px]">~{coverage}%</div>
          <div className="text-[11px] text-ink-40">охват РФ</div>
        </div>
      </div>
      <div className="inline-flex p-1 rounded-pill bg-ink-6 gap-1">
        <button onClick={() => setGeo({ mode: "all", regions: [] })}
          className={cn("px-3.5 py-1.5 rounded-pill text-[13px] font-display font-semibold transition-colors", !custom ? "bg-white text-ink" : "text-ink-60 hover:text-ink")}>РФ · все регионы</button>
        <button onClick={() => setGeo({ mode: "custom", regions: sel.length ? sel : ["msk", "spb"] })}
          className={cn("px-3.5 py-1.5 rounded-pill text-[13px] font-display font-semibold transition-colors", custom ? "bg-white text-ink" : "text-ink-60 hover:text-ink")}>Выбрать регионы</button>
      </div>
      {custom && (
        <div className="mt-4 fade-up">
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => {
              const selected = sel.includes(r.id);
              return (
                <span key={r.id}
                  className={cn("inline-flex items-center gap-1.5 h-9 pl-3 pr-1 rounded-pill border-2 text-[13px] font-display font-semibold transition-colors",
                    selected ? "bg-ink text-white border-ink" : "border-ink/15 text-ink hover:border-ink")}>
                  <button onClick={() => toggleRegion(r.id)} className="inline-flex items-center gap-1.5 pr-1.5">
                    {selected && <Check size={11} strokeWidth={3} />}
                    {r.label}
                    <span className="opacity-55 font-mono text-[11px]">{r.share}%</span>
                  </button>
                  {selected && (
                    <button onClick={() => toggleRegion(r.id)}
                      aria-label={`Убрать ${r.label}`}
                      className="w-6 h-6 grid place-items-center rounded-full hover:bg-white/15">
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  )}
                </span>
              );
            })}
            {customSel.map((id) => {
              const label = id.slice(CUSTOM_GEO_PREFIX.length);
              return (
                <span key={id}
                  className="inline-flex items-center gap-1.5 h-9 pl-3 pr-1 rounded-pill border-2 bg-ink text-white border-ink text-[13px] font-display font-semibold">
                  <span className="pr-1.5">{label}</span>
                  <button onClick={() => setGeo({ ...geo, regions: sel.filter((x) => x !== id) })}
                    aria-label={`Убрать ${label}`}
                    className="w-6 h-6 grid place-items-center rounded-full hover:bg-white/15">
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </span>
              );
            })}
          </div>
          <div className="mt-3 flex gap-2 items-start" ref={wrapRef}>
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Другой регион РФ (например: Краснодарский край)"
                value={customInput}
                onChange={(e) => { setCustomInput(e.target.value); setShowSuggestions(true); }}
                onFocus={() => customInput.trim() && setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                className="w-full h-9 px-3.5 rounded-pill border border-ink-12 bg-white text-[13px] focus:border-ink outline-none placeholder:text-ink-40"
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-ink-12 rounded-xl shadow-lg overflow-hidden">
                  {suggestions.map((s, i) => (
                    <li key={s}>
                      <button
                        type="button"
                        onPointerDown={(e) => { e.preventDefault(); addCustomRegion(s); }}
                        className={cn(
                          "w-full text-left px-4 py-2 text-[13px] transition-colors",
                          i === highlightIdx ? "bg-ink text-white" : "hover:bg-ink-6 text-ink",
                        )}>
                        {s}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button onClick={() => addCustomRegion()} disabled={!customInput.trim()}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-pill border-2 border-ink-12 text-[13px] font-display font-semibold text-ink hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
              <Plus size={13} strokeWidth={3} /> Добавить
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

const FORMAT_OPTS: {
  id: "seeding" | "native";
  label: string;
  platforms: Platform[];
  desc: string;
  steps: string[];
}[] = [
  {
    id: "seeding",
    label: "Посев",
    platforms: ["telegram", "vk", "max"],
    desc: "Вы загружаете готовый креатив, блогер публикует его в точности.",
    steps: ["Выбрать каналы", "Загрузить креатив", "Оплатить"],
  },
  {
    id: "native",
    label: "Натив",
    platforms: ["telegram", "vk", "youtube", "dzen", "tiktok"],
    desc: "Вы даёте ТЗ, блогер пишет пост от себя — больше доверия аудитории.",
    steps: ["Выбрать блогеров", "Составить ТЗ", "Оплатить"],
  },
];

function FormatBlock({ type, setType }: { type: "seeding" | "native"; setType: (t: "seeding" | "native") => void }) {
  return (
    <Card className="!p-6 mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
          Формат кампании
        </div>
        <div className="text-[11.5px] text-ink-40">Определяет весь сценарий работы</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {FORMAT_OPTS.map((f) => {
          const active = type === f.id;
          return (
            <button key={f.id} onClick={() => setType(f.id)}
              className={cn(
                "text-left p-4 rounded-md transition-all grid gap-3",
                active ? "bg-cream border-2 border-ink shadow-sm" : "bg-white border border-ink-12 hover:border-ink/30",
              )}>
              <div className="flex items-start justify-between gap-2">
                <span className="font-display font-bold text-[17px]">{f.label}</span>
                <div className="flex items-center gap-1 mt-0.5 flex-shrink-0">
                  {f.platforms.map((p) => (
                    <PlatformIcon key={p} platform={p} size={20} />
                  ))}
                </div>
              </div>
              <div className="text-[12.5px] text-ink-60 leading-relaxed">{f.desc}</div>
              <div className="flex flex-col gap-1 mt-0.5">
                {f.steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11.5px] text-ink-40">
                    <span className="w-4 h-4 rounded-full bg-ink-6 grid place-items-center font-mono text-[9px] font-bold flex-shrink-0">{i + 1}</span>
                    {s}
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

